import { useQuery } from "@apollo/client";
import moment from "moment";
import { useLocation, useParams } from "react-router-dom";
import {
  GetProfileOverviewQueryQuery,
  GetProfileOverviewQueryQueryVariables,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import WorkHistorySidebar from "@apps/work-history/components/work-history-sidebar";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import Sidebar from "@components/sidebar/sidebar";
import { getUrlUsingTypename } from "@helpers/helpers";

import ProfileSidebarHeader from "./components/header";
import getProfileOverviewQuery from "./graphql/get-profile-overview-query";
import ProfileActivities from "./overview-tab/components/profile-activities";
import ProfileArtifacts from "./overview-tab/components/profile-artifacts";
import ProfileCoachingInsights from "./overview-tab/components/profile-coaching-insights";
import ProfileLastOneonone from "./overview-tab/components/profile-last-oneonone";
import ProfileReviews from "./overview-tab/components/profile-reviews";
import ProfileContainer from "./profile-container";

const ProfileSidebar = () => {
  const link = useLink();
  const location = useLocation<TFLocationState>();
  const { profileId } = useParams<{
    profileId: string;
  }>();
  const currentUser = currentUserVar();
  const organization = currentOrganizationVar();
  const isAdmin = isAdminVar();

  const handleCloseSidebar = () => {
    if (location.state?.background) {
      return link.redirect(location.state.background);
    }
    link.redirect("/");
  };

  // variables
  const userId = profileId ? Number(profileId) : 0;
  const nextOneononeAfterDate = moment().startOf("hour").format();
  const lastOneononeBeforeDate = moment().startOf("hour").format();
  const workHistoryStartDate = moment()
    .subtract(7, "days")
    .format("YYYY-MM-DD");
  const workHistoryEndDate = moment().format("YYYY-MM-DD");
  const { data, loading } = useQuery<
    GetProfileOverviewQueryQuery,
    GetProfileOverviewQueryQueryVariables
  >(getProfileOverviewQuery, {
    variables: { userId, nextOneononeAfterDate, lastOneononeBeforeDate },
    skip: !profileId,
  });

  const profile = data?.user;
  const isManager = profile?.managersList.some(
    ({ id }) => id === currentUser.id,
  );

  return (
    <Sidebar
      onClose={handleCloseSidebar}
      className=""
      additionalActions={
        profile ? (
          <Button to={getUrlUsingTypename(profile)} text="Go to Profile" />
        ) : undefined
      }
    >
      {loading ? (
        <Loading className="mt-6">Loading profile...</Loading>
      ) : profile ? (
        <div className="px-6 bg-white flex flex-col gap-6 text-gray-900">
          {/* Profile name and image + close button */}
          <div className="flex items-center gap-2.5">
            <Avatar size="8" user={profile} />
            <div className="text-xl font-semibold">{profile.name}</div>
          </div>

          <ProfileSidebarHeader data={data} />

          {/* CONTENT */}
          {waffle.flag_is_active("coaching-insights") && (
            <ProfileCoachingInsights profile={profile} />
          )}

          <ProfileLastOneonone data={data} />

          {(isManager || profile.id === currentUser.id) &&
            organization.featureFlags.workHistory && (
              <ProfileContainer
                title="Work History"
                topRightContent={
                  <span className="text-gray-500 text-sm">
                    {moment(workHistoryStartDate).format("ll")} -{" "}
                    {moment(workHistoryEndDate).format("ll")}
                  </span>
                }
              >
                <WorkHistorySidebar
                  hideSummaryHeader
                  userId={profile.id}
                  startDate={workHistoryStartDate}
                  endDate={workHistoryEndDate}
                  sectionClassName="px-0"
                  topCollaboratorAvatarSize={5}
                />
              </ProfileContainer>
            )}

          <ProfileArtifacts profile={profile} />

          {(isAdmin || isManager || profile.id === currentUser.id) && (
            <ProfileReviews profileId={profile.id} />
          )}

          <ProfileActivities profileId={userId} />
        </div>
      ) : (
        <div className="px-6 bg-white flex flex-col gap-4">
          <div>We cannot find this profile.</div>
        </div>
      )}
    </Sidebar>
  );
};

export default ProfileSidebar;
